import { useMsal } from '@azure/msal-react';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import {
    FaCheckCircle,
    FaExclamationTriangle,
    FaFilter,
    FaSearch,
    FaShieldAlt,
    FaUserPlus
} from 'react-icons/fa';
import { FiFilter } from 'react-icons/fi';
import { GetUsers } from '../../_core/_request.ts';
import { UserType } from '../../Constants.ts';
import { AuthContext } from '../../context/AuthContext.js';
import { ThemeContext } from '../../context/ThemeContext.js';
import AccessTypeDropdown from '../../UIComponents/AccessTypeDropdown.tsx';
import { GetColumnRestrictions } from '../Analytics/_core/_request.ts';
import { UpsertUser } from '../Forms/UserRequest/_core/_request.ts';
import TooltipButton from '../Tools/TooltipButton.tsx';

interface AddAdUsersModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

interface ServerFilters {
    searchTerm: string;
    accountStatus: string;
    userType: string;
    department: string;
    jobTitle: string;
}

// Helper to convert Blob to Base64
const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
};

const fetchAdUsersFromGraph = async (instance: any, account: any, filters: ServerFilters, nextLink: string | null = null) => {
    try {
        const tokenResponse = await instance.acquireTokenSilent({
            scopes: ["User.Read.All"],
            account: account
        });

        const headers: Record<string, string> = {
            "Authorization": `Bearer ${tokenResponse.accessToken}`,
            "ConsistencyLevel": "eventual"
        };

        let endpoint = nextLink;

        if (!endpoint) {
            let baseSelect = "$select=id,displayName,givenName,surname,mail,userPrincipalName,jobTitle,department,country,city,officeLocation,mobilePhone,businessPhones,accountEnabled,userType,companyName,employeeType,onPremisesExtensionAttributes";
            let expandQuery = "$expand=manager($select=displayName,mail,userPrincipalName)";

            let filterConditions: string[] = [];

            if (filters.searchTerm) {
                const st = filters.searchTerm.trim().replace(/'/g, "''");
                filterConditions.push(`(startswith(givenName,'${st}') or startswith(surname,'${st}') or startswith(displayName,'${st}') or startswith(mail,'${st}') or startswith(userPrincipalName,'${st}'))`);
            }

            if (filters.accountStatus === 'active') filterConditions.push(`accountEnabled eq true`);
            if (filters.accountStatus === 'disabled') filterConditions.push(`accountEnabled eq false`);
            if (filters.userType !== 'all') filterConditions.push(`userType eq '${filters.userType}'`);

            if (filters.department.trim()) {
                const dept = filters.department.trim().replace(/'/g, "''");
                filterConditions.push(`startswith(department,'${dept}')`);
            }

            if (filters.jobTitle.trim()) {
                const role = filters.jobTitle.trim().replace(/'/g, "''");
                filterConditions.push(`startswith(jobTitle,'${role}')`);
            }

            let filterQuery = filterConditions.length > 0 ? `&$filter=${filterConditions.join(' and ')}` : '';
            endpoint = `https://graph.microsoft.com/v1.0/users?$count=true&${baseSelect}&${expandQuery}&$top=50${filterQuery}`;
        }

        const graphResponse = await fetch(endpoint, { method: 'GET', headers });

        if (!graphResponse.ok) {
            const errorText = await graphResponse.text();
            throw new Error(`Graph API returned ${graphResponse.status}: ${errorText}`);
        }

        const graphData = await graphResponse.json();

        return {
            users: graphData.value || [],
            nextLink: graphData['@odata.nextLink'] || null
        };
    } catch (error) {
        console.error("Graph API fetch failed.", error);
        throw error;
    }
};

export default function AddAdUsersModal({ isOpen, onClose, onSuccess }: AddAdUsersModalProps) {
    const { user: loggedInUser } = useContext(AuthContext);
    const { themeConfig } = useContext(ThemeContext);
    const { instance, accounts } = useMsal();

    // UPGRADED: Using a Map to store full user profile data instead of just a Set of emails
    const [existingEmails, setExistingEmails] = useState<Map<string, any>>(new Map());
    const [adUsers, setAdUsers] = useState<any[]>([]);
    const [restrictionGroups, setRestrictionGroups] = useState<any[]>([]);

    const [selectedUsers, setSelectedUsers] = useState<Map<string, any>>(new Map());

    const [nextLink, setNextLink] = useState<string | null>(null);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [activeFilters, setActiveFilters] = useState({
        searchTerm: '',
        accountStatus: 'all',
        userType: 'all',
        department: '',
        jobTitle: ''
    });

    const [debouncedFilters, setDebouncedFilters] = useState(activeFilters);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [isFetchingAd, setIsFetchingAd] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [results, setResults] = useState<{ success: number, failed: number, errors: string[] } | null>(null);

    useEffect(() => {
        if (!isOpen) return;
        let isMounted = true;
        setActiveFilters({ searchTerm: '', accountStatus: 'all', userType: 'all', department: '', jobTitle: '' });
        setResults(null);
        setSelectedUsers(new Map());
        setIsInitialLoad(true);

        const loadInitialData = async () => {
            try {
                const localRes = await GetUsers({ userID: 0, userName: "", email: "", roleID: null, roleGroupID: null, managerUserID: null, appliationAccess: null });
                if (!isMounted) return;
                
                // Store the full user objects in the Map
                const usersMap = new Map();
                localRes.data.forEach((u: any) => {
                    if (u.email) usersMap.set(u.email.toLowerCase(), u);
                });
                setExistingEmails(usersMap);

                const groupsRes = await GetColumnRestrictions({ columnRestrictionId: 0, columns: null, users: null, name: null });
                if (isMounted) setRestrictionGroups(groupsRes.data || []);

            } catch (err) {
                console.error("Failed to fetch initial data", err);
            } finally {
                if (isMounted) setIsInitialLoad(false);
            }
        };

        loadInitialData();
        return () => { isMounted = false; };
    }, [isOpen]);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedFilters(activeFilters), 700);
        return () => clearTimeout(timer);
    }, [activeFilters]);

    useEffect(() => {
        if (!isOpen || accounts.length === 0 || isInitialLoad) return;

        let isMounted = true;
        setIsFetchingAd(true);

        fetchAdUsersFromGraph(instance, accounts[0], debouncedFilters, null)
            .then(data => {
                if (!isMounted) return;
                const filtered = data.users.filter((u: any) => {
                    const email = (u.mail || u.userPrincipalName || "").toLowerCase();
                    return Boolean(email);
                });
                setAdUsers(filtered);
                setNextLink(data.nextLink);
            })
            .catch(err => console.error("Search failed", err))
            .finally(() => {
                if (isMounted) setIsFetchingAd(false);
            });

        return () => { isMounted = false; };
    }, [debouncedFilters, accounts, instance, isOpen, isInitialLoad, existingEmails]);

    const loadMoreUsers = useCallback(async () => {
        if (!nextLink || isFetchingMore || accounts.length === 0) return;
        setIsFetchingMore(true);
        try {
            const data = await fetchAdUsersFromGraph(instance, accounts[0], debouncedFilters, nextLink);
            const filtered = data.users.filter((u: any) => {
                const email = (u.mail || u.userPrincipalName || "").toLowerCase();
                return Boolean(email);
            });
            setAdUsers(prev => [...prev, ...filtered]);
            setNextLink(data.nextLink);
        } catch (err) {
            console.error("Failed to load more users", err);
        } finally {
            setIsFetchingMore(false);
        }
    }, [nextLink, isFetchingMore, accounts, instance, debouncedFilters, existingEmails]);

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
        if (scrollHeight - scrollTop <= clientHeight + 150) {
            loadMoreUsers();
        }
    };

    const toggleUserSelection = (adUser: any, isChecked: boolean) => {
        if (results) setResults(null);
        const newSelection = new Map(selectedUsers);
        const email = (adUser.mail || adUser.userPrincipalName).toLowerCase();

        if (isChecked) {
            newSelection.set(email, {
                ...adUser,
                applicationAccess: UserType.User,
                oprid: email,
                assignedGroupId: "", 
                syncStatus: 'idle',
                syncError: ''
            });
        } else {
            const existingUser = newSelection.get(email);
            if (existingUser?.syncStatus !== 'success') {
                newSelection.delete(email);
            }
        }
        setSelectedUsers(newSelection);
    };

    const handleAccessChange = (email: string, accessLevel: number) => {
        if (results) setResults(null);
        const newSelection = new Map(selectedUsers);
        const userObj = newSelection.get(email.toLowerCase());
        if (userObj) {
            userObj.applicationAccess = accessLevel;
            if (userObj.syncStatus === 'error') {
                userObj.syncStatus = 'idle';
                userObj.syncError = '';
            }
            newSelection.set(email.toLowerCase(), userObj);
            setSelectedUsers(newSelection);
        }
    };

    const handleGroupChange = (email: string, groupId: string | number) => {
        if (results) setResults(null);
        const newSelection = new Map(selectedUsers);
        const userObj = newSelection.get(email.toLowerCase());
        if (userObj) {
            userObj.assignedGroupId = groupId;
            if (userObj.syncStatus === 'error') {
                userObj.syncStatus = 'idle';
                userObj.syncError = '';
            }
            newSelection.set(email.toLowerCase(), userObj);
            setSelectedUsers(newSelection);
        }
    };

    const fetchUserProfilePicture = async (userId: string) => {
        try {
            const tokenResponse = await instance.acquireTokenSilent({
                scopes: ["User.Read.All"],
                account: accounts[0]
            });
            const response = await fetch(`https://graph.microsoft.com/v1.0/users/${userId}/photo/$value`, {
                headers: { "Authorization": `Bearer ${tokenResponse.accessToken}` }
            });
            if (response.ok) {
                const blob = await response.blob();
                return await blobToBase64(blob);
            }
        } catch (e) {
            console.warn(`Could not fetch photo for user ${userId}`, e);
        }
        return null;
    };

    const handleSave = async () => {
        const usersToSave = Array.from(selectedUsers.values()).filter(u => u.syncStatus !== 'success');

        if (usersToSave.length === 0) return;

        setIsSaving(true);
        let successCount = 0;
        let failCount = 0;
        const currentErrors: string[] = [];

        for (const adUser of usersToSave) {
            const email = adUser.mail || adUser.userPrincipalName;
            const emailKey = email.toLowerCase();
            const nameParts = (adUser.displayName || "").split(" ");
            const firstName = adUser.givenName || nameParts[0] || "Unknown";
            const lastName = adUser.surname || nameParts.slice(1).join(" ") || "Unknown";

            const photoBase64 = await fetchUserProfilePicture(adUser.id);

            const requestData: any = {
                userID: 0,
                userName: `${firstName} ${lastName}`,
                userFirstName: firstName,
                userLastName: lastName,
                email: email,
                oprid: adUser.oprid,
                profilePicture: photoBase64,
                status: adUser.accountEnabled !== false ? "active" : "inactive",
                applicationAccess: adUser.applicationAccess,
                columnRestrictionGroupId: adUser.assignedGroupId ? parseInt(adUser.assignedGroupId) : null,
                isAdUser: 1,
                byUser: loggedInUser?.userID || 1,
                jobTitle: adUser.jobTitle,
                department: adUser.onPremisesExtensionAttributes?.extensionAttribute6 || adUser.department,
                country: adUser.country,
                city: adUser.city,
                officeLocation: adUser.officeLocation,
                mobilePhone: adUser.mobilePhone,
                accountEnabled: adUser.accountEnabled,
                companyName: adUser.companyName,
                employeeType: adUser.employeeType,
                manager: adUser.manager,
                userPrincipalName: adUser.userPrincipalName,
                adUserId: adUser.id
            };

            try {
                const response = await UpsertUser(requestData);
                const responseData = response?.data !== undefined ? response.data : response;

                if (typeof responseData === 'string' && responseData.trim() !== '') {
                    throw new Error(responseData);
                }

                successCount++;
                setSelectedUsers(prev => {
                    const m = new Map(prev);
                    const u = m.get(emailKey);
                    if (u) m.set(emailKey, { ...u, syncStatus: 'success', syncError: '' });
                    return m;
                });

            } catch (err: any) {
                failCount++;
                const errMsg = err instanceof Error ? err.message : (err.response?.data || (typeof err === 'string' ? err : 'Unknown error'));
                currentErrors.push(`${email}: ${errMsg}`);

                setSelectedUsers(prev => {
                    const m = new Map(prev);
                    const u = m.get(emailKey);
                    if (u) m.set(emailKey, { ...u, syncStatus: 'error', syncError: errMsg });
                    return m;
                });
            }
        }

        setIsSaving(false);
        setResults({ success: successCount, failed: failCount, errors: currentErrors });

        if (failCount === 0) {
            const hasAnyErrors = Array.from(selectedUsers.values()).some(u => u.syncStatus === 'error');
            if (!hasAnyErrors) {
                setTimeout(() => {
                    onSuccess();
                    onClose();
                }, 1500);
            }
        } else if (successCount > 0) {
            onSuccess();
        }
    };

    const handleSelectAll = (isChecked: boolean) => {
        if (results) setResults(null);
        const newSelection = new Map(selectedUsers);
        if (isChecked) {
            adUsers.forEach(u => {
                const email = (u.mail || u.userPrincipalName).toLowerCase();
                if (!existingEmails.has(email) && !newSelection.has(email)) {
                    newSelection.set(email, {
                        ...u,
                        applicationAccess: UserType.User,
                        oprid: email,
                        assignedGroupId: "",
                        syncStatus: 'idle'
                    });
                }
            });
        } else {
            adUsers.forEach(u => {
                const email = (u.mail || u.userPrincipalName).toLowerCase();
                const existingUser = newSelection.get(email);
                if (existingUser && existingUser.syncStatus !== 'success') {
                    newSelection.delete(email);
                }
            });
        }
        setSelectedUsers(newSelection);
    };

    const pendingUsers = Array.from(selectedUsers.values()).filter(u => u.syncStatus !== 'success');
    const pendingCount = pendingUsers.length;

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="card shadow-lg d-flex flex-column overflow-hidden"
                    style={{ width: '95%', maxWidth: '1600px', height: '90vh' }}
                >
                    <div className="card-header d-flex justify-content-between align-items-center py-3 border-bottom">
                        <h5 className="mb-0 fw-bold d-flex align-items-center gap-2">
                            <FaUserPlus className="text-primary" /> Import Active Directory Users
                        </h5>
                        <button className="btn-close shadow-none" onClick={onClose} disabled={isSaving}></button>
                    </div>

                    {results && (
                        <div className={`alert m-3 mb-0 ${results.failed > 0 ? 'alert-warning' : 'alert-success'}`}>
                            <h6 className="fw-bold mb-1">Import Batch Complete</h6>
                            <p className="mb-0 small">Successfully added <strong>{results.success}</strong> users. {results.failed > 0 && `Failed: ${results.failed}`}</p>
                        </div>
                    )}

                    <div className="card-body d-flex flex-column p-0" style={{ overflow: 'hidden' }}>
                        <div className="p-3 border-bottom d-flex flex-column gap-2">
                            <div className='gap-2' style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <div className="input-group j_srch">
                                    <span className="input-group-text text-muted-theme bg-theme-card">
                                        {isFetchingAd ? <Spinner animation="border" size="sm" className="text-primary" /> : <FaSearch />}
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control search-control-with-button"
                                        placeholder="Search by name or email (e.g. John Doe)..."
                                        value={activeFilters.searchTerm}
                                        onChange={(e) => setActiveFilters(p => ({ ...p, searchTerm: e.target.value }))}
                                        disabled={isInitialLoad || isSaving}
                                    />
                                    <TooltipButton title={showFilters ? "Click to hide advanced filter options" : "Click to view advanced filter"}>
                                        <button className='btn btn-primary py-2 j_fil' onClick={() => setShowFilters(!showFilters)}>
                                            <div className='d-flex flex-row align-items-center gap-1'>
                                                {showFilters ? <FiFilter /> : <FaFilter />}
                                                {showFilters ? 'Hide filter' : 'Show filter'}
                                            </div>
                                        </button>
                                    </TooltipButton>
                                </div>
                            </div>

                            <AnimatePresence>
                                {showFilters && (
                                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }} style={{
                                            backgroundColor: themeConfig?.colors?.card || 'var(--bs-tertiary-bg)',
                                            color: themeConfig?.colors?.text || 'inherit',
                                            border: `1px solid ${themeConfig?.colors?.border || 'var(--bs-border-color)'}`
                                        }} className="mt-1 overflow-hidden rounded-2 shadow-sm px-3 py-2">
                                        <div className="row g-3">
                                            <div className="col-md-2">
                                                <label className="form-label small fw-bold opacity-75">Status</label>
                                                <select className="form-select form-select-sm" value={activeFilters.accountStatus} onChange={(e) => setActiveFilters(p => ({ ...p, accountStatus: e.target.value }))}>
                                                    <option value="all">All</option>
                                                    <option value="active">Active Only</option>
                                                    <option value="disabled">Disabled Only</option>
                                                </select>
                                            </div>
                                            <div className="col-md-2">
                                                <label className="form-label small fw-bold opacity-75">Type</label>
                                                <select className="form-select form-select-sm" value={activeFilters.userType} onChange={(e) => setActiveFilters(p => ({ ...p, userType: e.target.value }))}>
                                                    <option value="all">All</option>
                                                    <option value="Member">Internal</option>
                                                    <option value="Guest">External</option>
                                                </select>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label small fw-bold opacity-75">Department</label>
                                                <input type="text" className="form-control form-control-sm" placeholder="e.g. IT, Sales" value={activeFilters.department} onChange={(e) => setActiveFilters(p => ({ ...p, department: e.target.value }))} />
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label small fw-bold opacity-75">Job Title</label>
                                                <input type="text" className="form-control form-control-sm" placeholder="e.g. Manager" value={activeFilters.jobTitle} onChange={(e) => setActiveFilters(p => ({ ...p, jobTitle: e.target.value }))} />
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        <div className="d-flex flex-column px-4 mt-2" onScroll={handleScroll} style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                                <thead style={{ position: 'sticky', top: 0, zIndex: 10 }} className="table-thead">
                                    <tr className='bg-theme-card'>
                                        <th className='text-color table-th' style={{ fontWeight: "normal", fontSize: "12px", padding: '12px 4px 12px 12px', textAlign: 'center', verticalAlign: 'middle', width: '50px' }}>
                                            <div className="w-100 h-100 d-flex flex-row align-items-center justify-content-center">
                                                <input type="checkbox" className="form-check-input shadow-none cursor-pointer m-0"
                                                    onChange={(e) => handleSelectAll(e.target.checked)}
                                                    checked={
                                                        adUsers.filter(u => !existingEmails.has((u.mail || u.userPrincipalName).toLowerCase())).length > 0 &&
                                                        adUsers.filter(u => !existingEmails.has((u.mail || u.userPrincipalName).toLowerCase())).every(u => selectedUsers.has((u.mail || u.userPrincipalName).toLowerCase()))
                                                    } />
                                            </div>
                                        </th>
                                        <th className='text-color table-th' style={{ fontWeight: "normal", fontSize: "12px", padding: '12px 4px', textAlign: 'left', verticalAlign: 'middle', minWidth: '300px' }}>User Details</th>
                                        <th className='text-color table-th' style={{ fontWeight: "normal", fontSize: "12px", padding: '12px 4px', textAlign: 'left', verticalAlign: 'middle', minWidth: '150px' }}>Job Title</th>
                                        <th className='text-color table-th' style={{ fontWeight: "normal", fontSize: "12px", padding: '12px 4px', textAlign: 'left', verticalAlign: 'middle', minWidth: '130px' }}>Department</th>
                                        <th className='text-color table-th' style={{ fontWeight: "normal", fontSize: "12px", padding: '12px 4px', textAlign: 'left', verticalAlign: 'middle', minWidth: '110px' }}>Status</th>
                                        <th className='text-color table-th' style={{ fontWeight: "normal", fontSize: "12px", padding: '12px 4px', textAlign: 'left', verticalAlign: 'middle', minWidth: '160px' }}>Access Level</th>
                                        <th className='text-color table-th' style={{ fontWeight: "normal", fontSize: "12px", padding: '12px 4px', textAlign: 'left', verticalAlign: 'middle', minWidth: '220px' }}>
                                            <div className='d-flex align-items-center gap-1'><FaShieldAlt /> Restriction Group</div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="table-tbody">
                                    {isInitialLoad || (isFetchingAd && adUsers.length === 0) ? (
                                        [...Array(6)].map((_, i) => (
                                            <tr key={`loading-${i}`}>
                                                {[...Array(7)].map((_, j) => (
                                                    <td key={`cell-${j}`} style={{ padding: j > 0 ? '12px 4px' : '12px 4px 12px 12px', verticalAlign: 'middle' }}>
                                                        <div className="placeholder-glow">
                                                            <span className="placeholder col-10"></span>
                                                        </div>
                                                    </td>
                                                ))}
                                            </tr>
                                        ))
                                    ) : adUsers.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} style={{ padding: '40px 0' }}>
                                                <div className="d-flex flex-column align-items-center justify-content-center h-100 opacity-50">
                                                    <FaExclamationTriangle size={40} />
                                                    <h6 className="mt-3">No matching users found</h6>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        adUsers.map((u) => {
                                            const emailKey = (u.mail || u.userPrincipalName).toLowerCase();
                                            const userInSelection = selectedUsers.get(emailKey);
                                            
                                            const isExisting = existingEmails.has(emailKey);
                                            const existingData = isExisting ? existingEmails.get(emailKey) : null;
                                            const isSelected = !!userInSelection;

                                            const isSuccess = userInSelection?.syncStatus === 'success';
                                            const isError = userInSelection?.syncStatus === 'error';

                                            // Compact padding for existing users
                                            const padY = isExisting ? '6px' : '12px';

                                            return (
                                                <motion.tr layout transition={{ duration: 0.2 }} key={u.id} className={isSuccess ? 'table-success' : isSelected ? 'table-primary-subtle' : ''}
                                                    style={{
                                                        opacity: isExisting ? 0.65 : 1,
                                                        backgroundColor: isExisting ? themeConfig?.colors?.disabledBackground : 'transparent',
                                                        pointerEvents: isExisting ? 'none' : 'auto' 
                                                    }}>
                                                    <td className="text-center" style={{ padding: `${padY} 4px ${padY} 12px`, color: themeConfig?.colors?.text, verticalAlign: 'middle' }}>
                                                        <input type="checkbox" className="form-check-input shadow-none m-0"
                                                            checked={isSelected || isExisting}
                                                            onChange={(e) => toggleUserSelection(u, e.target.checked)}
                                                            disabled={isSaving || isSuccess || isExisting}
                                                            style={{ cursor: isExisting ? 'not-allowed' : 'pointer', opacity: isExisting ? 0.4 : 1 }} />
                                                    </td>
                                                    <td style={{ padding: `${padY} 4px`, color: themeConfig?.colors?.text, verticalAlign: 'middle' }}>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div className="bg-primary-subtle text-primary d-flex align-items-center justify-content-center rounded-circle fw-bold" 
                                                                 style={{ width: isExisting ? '26px' : '36px', height: isExisting ? '26px' : '36px', fontSize: isExisting ? '11px' : 'inherit', flexShrink: 0 }}>
                                                                {u.displayName?.charAt(0) || '?'}
                                                            </div>
                                                            <div className="overflow-hidden">
                                                                <div className="fw-bold text-truncate d-flex align-items-center gap-2" style={{ maxWidth: '250px', fontSize: isExisting ? '13px' : 'inherit' }}>
                                                                    {u.displayName}
                                                                    {isExisting && (
                                                                        <span className="badge bg-secondary bg-opacity-50 text-white-black border border-secondary" style={{ fontSize: '9px', fontWeight: 600, padding: '3px 6px' }}>
                                                                            In System
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <small className="text-muted text-truncate d-block" style={{ maxWidth: '250px', fontSize: isExisting ? '11px' : 'inherit' }}>{u.mail || u.userPrincipalName}</small>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: `${padY} 4px`, color: themeConfig?.colors?.text, verticalAlign: 'middle' }}>
                                                        <div className={`fw-semibold ${isExisting ? 'small' : ''}`} style={{ fontSize: isExisting ? '12px' : 'inherit' }}>{u.jobTitle || '--'}</div>
                                                    </td>
                                                    <td style={{ padding: `${padY} 4px`, color: themeConfig?.colors?.text, verticalAlign: 'middle' }}>
                                                        <div className="text-muted" style={{ fontSize: isExisting ? '12px' : 'inherit' }}>
                                                            {u.onPremisesExtensionAttributes?.extensionAttribute6 || u.department || '--'}
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: `${padY} 4px`, color: themeConfig?.colors?.text, verticalAlign: 'middle' }}>
                                                        <span className={`badge rounded-pill fw-normal ${u.accountEnabled !== false ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'}`} style={{ fontSize: isExisting ? '10px' : 'inherit'}}>
                                                            {u.accountEnabled !== false ? 'Active' : 'Disabled'}
                                                        </span>
                                                        <div className="text-muted mt-1" style={{ fontSize: isExisting ? '11px' : '12px' }}>{u.userType || 'Member'}</div>

                                                        {isSelected && isError && (
                                                            <div className="text-danger small mt-2 d-flex align-items-start gap-1">
                                                                <FaExclamationTriangle className="mt-1 flex-shrink-0" size={11} />
                                                                <span>{userInSelection.syncError}</span>
                                                            </div>
                                                        )}
                                                        {isSelected && isSuccess && (
                                                            <div className="text-success small mt-2 d-flex align-items-center gap-1 fw-bold">
                                                                <FaCheckCircle size={12} /> Saved Successfully
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: `${padY} 4px`, color: themeConfig?.colors?.text, verticalAlign: 'middle' }}>
                                                        {isSelected ? (
                                                            <AccessTypeDropdown
                                                                disabled={isSaving || isSuccess}
                                                                initVal={userInSelection.applicationAccess}
                                                                onChange={(val) => handleAccessChange(emailKey, val)}
                                                            />
                                                        ) : isExisting ? (
                                                            <span className="badge border border-secondary text-secondary" style={{ fontSize: '11px', padding: '4px 8px', fontWeight: 500 }}>
                                                                {existingData?.applicationAccess === UserType.Admin ? 'Admin' : 'User'}
                                                            </span>
                                                        ) : '--'}
                                                    </td>
                                                    <td style={{ padding: `${padY} 4px`, color: themeConfig?.colors?.text, verticalAlign: 'middle' }}>
                                                        {isSelected ? (
                                                            <select
                                                                className="form-select form-select-sm border shadow-none"
                                                                value={userInSelection.assignedGroupId || ""}
                                                                onChange={(e) => handleGroupChange(emailKey, e.target.value)}
                                                                disabled={isSaving || isSuccess}
                                                                style={{ minWidth: '180px' }}
                                                            >
                                                                <option value="">No Restriction</option>
                                                                {restrictionGroups.map(group => (
                                                                    <option key={group.columnRestrictionId} value={group.columnRestrictionId}>
                                                                        {group.name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        ) : isExisting ? (
                                                            <span className="badge border border-secondary text-secondary text-truncate" style={{ fontSize: '11px', padding: '4px 8px', fontWeight: 500, maxWidth: '180px', display: 'inline-block' }}>
                                                                {restrictionGroups.find(g => g.columnRestrictionId === existingData?.columnRestrictionGroupId)?.name || "No Restriction"}
                                                            </span>
                                                        ) : '--'}
                                                    </td>
                                                </motion.tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                            {isFetchingMore && <div className="text-center py-3"><Spinner size="sm" /> <small className="ms-2">Fetching more results...</small></div>}
                        </div>
                    </div>

                    <div className="card-footer p-3 border-top d-flex justify-content-between align-items-center">
                        <div>
                            <span className="badge bg-primary px-3 py-2 shadow-sm">{selectedUsers.size} Total Selected</span>
                            {pendingCount > 0 && <span className="ms-2 text-muted small fw-bold">({pendingCount} pending submission)</span>}
                        </div>
                        <div className="d-flex gap-2">
                            <button className="btn btn-link text-decoration-none text-muted" onClick={onClose} disabled={isSaving}>Close</button>
                            <button className="btn btn-primary px-4 shadow-sm d-flex align-items-center gap-2"
                                onClick={handleSave} disabled={pendingCount === 0 || isSaving}>
                                {isSaving ? <><Spinner size="sm" animation="border" /> Importing...</> : `Import ${pendingCount} Selected`}
                            </button>
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
